import { useMemo } from 'react'

import { AffModels } from '@affiliate-cabinet/graphql'
import { useI18n } from '@affiliate-cabinet/i18n'
import { getApiV2Offers, useQuery } from '@affiliate-cabinet/rest'
import { OffersTypesComponents, ResponseErrorInterface } from '@affiliate-cabinet/types'

import { affModelEntity } from '../../../constants/affModel.entity'

import { useApiAdvertisersIdModelsTranslationKeys } from './translations/useApiAdvertisersIdModels.translationKeys'

const getModelDescription = (
  model: { model: string; offerId: number },
  unavailableModels: { model: string; offerId: number }[],
  modelsWithUnacceptedRules: { model: string; offerId: number }[],
  i18n: ReturnType<typeof useI18n>['i18n'],
): string | undefined => {
  const offerUnavailableModel = unavailableModels?.some((unavailableModel) => unavailableModel.model === model.model)

  if (offerUnavailableModel) {
    return i18n.t(useApiAdvertisersIdModelsTranslationKeys.offerModelsOfferStatusIsInactive)
  }

  const offerModelWithUnacceptedRules = modelsWithUnacceptedRules?.some((modelWithUnacceptedRules) => modelWithUnacceptedRules.model === model.model)

  if (offerModelWithUnacceptedRules) {
    return i18n.t(useApiAdvertisersIdModelsTranslationKeys.offerModelsOfferRulesAreNotAccepted)
  }

  return undefined
}

export const useOffersModelsByAdvertiserId = (advertiserId: number) => {
  const { i18n } = useI18n()
  const {
    data: availableOffers,
    error: availableOffersError,
    isFetching: availableOffersIsLoading,
  } = useQuery<unknown, ResponseErrorInterface, OffersTypesComponents['schemas']['OffersCollectionV2']>(
    ['useOffersModelsByAdvertiserId-getApiOffersAvailable', advertiserId],
    () =>
      getApiV2Offers({
        advertiserIds: [Number(advertiserId)],
        status: 'available',
      }),
    {
      keepPreviousData: true,
    },
  )

  const {
    data: unavailableOffers,
    error: unavailableOffersError,
    isFetching: unavailableOffersIsLoading,
  } = useQuery<unknown, ResponseErrorInterface, OffersTypesComponents['schemas']['OffersCollectionV2']>(
    ['useOffersModelsByAdvertiserId-getApiOffersUnavailable', advertiserId],
    () =>
      getApiV2Offers({
        advertiserIds: [Number(advertiserId)],
        status: 'unavailable',
      }),
    {
      keepPreviousData: true,
    },
  )

  const preparedModels = useMemo(() => {
    const availableOffersData = availableOffers?.data || []
    const unavailableOffersData = unavailableOffers?.data || []

    const availableModels = availableOffersData.map((offer) => ({
      model: offer.model,
      offerId: offer.id,
    }))
    const unavailableModels = unavailableOffersData.map((offer) => ({
      model: offer.model,
      offerId: offer.id,
    }))

    const models = [...availableModels, ...unavailableModels]

    const availableModelsWithAcceptedRules = availableOffersData
      .filter((offer) => offer.marketingRulesAccepted)
      .map((offer) => ({
        model: offer.model,
        offerId: offer.id,
      }))

    const modelsWithUnacceptedRules = [...availableOffersData, ...unavailableOffersData]
      .filter((offer) => !offer.marketingRulesAccepted)
      .map((offer) => ({
        model: offer.model,
        offerId: offer.id,
      }))

    const getDefaultModel = () => {
      if (availableModelsWithAcceptedRules && availableModelsWithAcceptedRules.length > 0) {
        return availableModelsWithAcceptedRules[0]
      }

      if (availableModels && availableModels.length > 0) {
        return availableModels[0]
      }

      return unavailableModels?.[0]
    }

    return {
      availableModels,
      getDefaultModel,
      models: models.map((model) => ({
        label: affModelEntity.KEY_TO_TRANSLATED_VALUE[(model.model as unknown as string)?.toUpperCase() as AffModels],
        description: getModelDescription(model, unavailableModels, modelsWithUnacceptedRules, i18n),
        value: model.model,
      })),
      modelsWithUnacceptedRules,
      unavailableModels,
    }
  }, [availableOffers?.data, i18n, unavailableOffers?.data])

  return {
    advertisersModels: preparedModels.models,
    availableModels: preparedModels.availableModels,
    availableOffersError,
    defaultModel: preparedModels.getDefaultModel(),
    isFetching: availableOffersIsLoading || unavailableOffersIsLoading,
    modelsWithUnacceptedRules: preparedModels.modelsWithUnacceptedRules,
    unavailableModels: preparedModels.unavailableModels,
    unavailableOffersError,
  }
}
